import React, { useState } from 'react';

/**
 * Goals Guidance Component
 * Provides specialized guidance for goal-setting tasks (HMS, Quality, Environment)
 */
function GoalsGuidance({ task, fileInputRef, uploading, files, onFileUpload }) {
  const [showUpload, setShowUpload] = useState(false);
  const hasFiles = files && files.length > 0;

  const goalExamples = [
    { area: 'Sykefravær', example: 'Redusere sykefraværet fra 6% til 4% innen 12 måneder', type: 'HMS' },
    { area: 'Avvik', example: 'Lukke alle HMS-avvik innen 30 dager', type: 'HMS' },
    { area: 'Energi', example: 'Redusere energiforbruket med 10% i 2026', type: 'Miljø' },
    { area: 'Avfall', example: 'Kildesortere 80% av avfallet', type: 'Miljø' },
    { area: 'Kompetanse', example: 'Alle ansatte gjennomfører HMS-kurs', type: 'HMS' },
    { area: 'Kundekvalitet', example: '95% kundetilfredshet', type: 'Kvalitet' },
  ];

  const smartCriteria = [
    { letter: 'S', word: 'Spesifikt', description: 'Hva skal oppnås konkret?' },
    { letter: 'M', word: 'Målbart', description: 'Hvordan måler vi det?' },
    { letter: 'A', word: 'Oppnåelig', description: 'Er det realistisk?' },
    { letter: 'R', word: 'Relevant', description: 'Støtter det virksomheten?' },
    { letter: 'T', word: 'Tidsbundet', description: 'Når skal det være oppnådd?' },
  ];

  return (
    <div data-testid="goals-guidance">
      {/* Why this is important */}
      <div className="mb-6 bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-teal-900 mb-1">Retning for HMS-arbeidet</p>
            <p className="text-sm text-teal-800">
              Tydelige mål gir noe å jobbe mot og gjør det mulig å måle fremgang. 
              Uten mål blir HMS-arbeidet tilfeldig og vanskelig å forbedre.
            </p>
          </div>
        </div>
      </div>

      {/* SMART criteria */}
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
        SMART-kriterier for gode mål
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-5 gap-2">
          {smartCriteria.map((item, idx) => (
            <div key={idx} className="text-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1">
                <span className="text-blue-700 font-bold">{item.letter}</span>
              </div>
              <p className="text-xs font-medium text-gray-900">{item.word}</p>
              <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Goal examples */}
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
        Eksempler på mål
      </p>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
        <div className="space-y-2">
          {goalExamples.map((goal, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-900">{goal.area}</p>
                <p className="text-xs text-gray-500">{goal.example}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                goal.type === 'HMS' ? 'bg-amber-100 text-amber-700' :
                goal.type === 'Miljø' ? 'bg-green-100 text-green-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {goal.type}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-green-900 mb-2">Tips</h4>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• Start med 2-3 mål, ikke for mange</li>
          <li>• Involver ansatte i målsettingen</li>
          <li>• Følg opp målene jevnlig (månedlig/kvartalsvis)</li>
          <li>• Juster mål som viser seg urealistiske</li>
        </ul>
      </div>

      {/* Upload option */}
      <div className="border border-gray-200 rounded-xl p-4">
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="w-full flex items-center gap-3 text-left"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <span className="font-medium text-gray-900">Last opp måldokument</span>
            <p className="text-sm text-gray-500 mt-0.5">
              Har du allerede definerte mål?
            </p>
          </div>
          <svg className={`w-5 h-5 text-gray-400 transition-transform ${showUpload ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showUpload && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Laster opp...' : 'Last opp dokument'}
            </button>
            {hasFiles && (
              <p className="text-xs text-green-600 mt-2 text-center">
                ✓ {files.length} dokument(er) lastet opp
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GoalsGuidance;
